import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Separator } from "./ui/separator";
import { FileText, RefreshCw, Save, Mail, Download, ShoppingCart, Calculator, FolderOpen } from "lucide-react";

export function QuoteSummary({ 
  quote,
  region,
  onRegionChange,
  includePackaging,
  onIncludePackagingChange,
  isCalculating,
  onRefresh,
  onSave,
  onEmail,
  onDownloadPDF,
  onProceed,
  savedQuoteId,
  onLoadQuote,
  embedded = false
}) {
  return (
    <div className={embedded ? "" : "sticky top-28"}>
      <Card className="shadow-lg border-gray-200">
        <CardHeader className="bg-[#6A7359] text-white rounded-t-lg">
          <div className="flex justify-between items-center">
            <CardTitle className="flex gap-2 font-sans">
              <FileText className="w-5 h-5" />
              Quote Summary
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onRefresh} 
              disabled={isCalculating} 
              className="text-white hover:bg-white/20" 
              data-testid="refresh-quote-btn"
            >
              <RefreshCw className={"w-4 h-4 " + (isCalculating ? "animate-spin" : "")} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Region Selector */}
          <div className="mb-4">
            <Label className="text-sm text-gray-500">Shipping Region</Label>
            <Select value={region} onValueChange={onRegionChange}>
              <SelectTrigger className="border-gray-200" data-testid="region-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="central_canada">Central Canada</SelectItem>
                <SelectItem value="eastern_canada">Eastern Canada</SelectItem>
                <SelectItem value="western_canada">Western Canada</SelectItem>
                <SelectItem value="local_pickup">Local Pickup (Free)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Optional packaging for local pickup */}
          {region === "local_pickup" && (
            <div className="mb-4 flex items-center gap-2">
              <Checkbox 
                checked={includePackaging} 
                onCheckedChange={onIncludePackagingChange} 
                id="include-packaging" 
                data-testid="include-packaging" 
              />
              <Label htmlFor="include-packaging" className="text-sm cursor-pointer">
                Include packaging (${quote ? (quote.packaging_fee > 0 ? quote.packaging_fee.toFixed(0) : "30-180") : "30-180"})
              </Label>
            </div>
          )}
          
          {quote ? (
            <div>
              {/* Line Items */}
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {quote.doors.map((x, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <div>
                      <span className="font-medium text-[#1A1A1A]">{x.quantity}× {x.style}</span>
                      <span className="block text-xs text-gray-500">
                        {x.width.toFixed(4)}×{x.height.toFixed(4)}&quot; ({x.total_sqft.toFixed(4)} sqft)
                      </span>
                      {(x.hinges > 0 || x.primed) && (
                        <span className="text-xs text-[#6A7359]">
                          {x.hinges > 0 && x.hinges + " hinges "}
                          {x.primed && "+ Primed"}
                        </span>
                      )}
                    </div>
                    <span className="font-medium">${x.line_total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <Separator className="my-3 bg-gray-200" />
              
              {/* Totals */}
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal ({quote.total_sqft.toFixed(4)} sqft)</span>
                  <span>${quote.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Setup Fee</span>
                  <span>${quote.setup_fee.toFixed(2)}</span>
                </div>
                {region === "local_pickup" ? (
                  includePackaging && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Packaging Fee</span>
                      <span>${quote.packaging_fee.toFixed(2)}</span>
                    </div>
                  )
                ) : (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Packaging Fee</span>
                    <span>${quote.packaging_fee.toFixed(2)}</span>
                  </div>
                )}
                {region !== "local_pickup" && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Shipping ({(quote.shipping_rate*100).toFixed(0)}%)</span>
                    <span>${quote.shipping.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <Separator className="my-3 bg-gray-200" />
              
              {/* Grand Total */}
              <div className="flex justify-between text-xl font-bold text-[#6A7359] mb-4">
                <span>Total</span>
                <span>${quote.total.toFixed(2)}</span>
              </div>
              
              {savedQuoteId && (
                <p className="text-xs text-center text-[#6A7359] mb-2">Quote ID: {savedQuoteId}</p>
              )}
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onSave} 
                  className="border-[#6A7359] text-[#6A7359] hover:bg-[#6A7359]/10" 
                  data-testid="save-quote-btn"
                >
                  <Save className="w-3 h-3 mr-1" />
                  Save
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onEmail} 
                  className="border-[#6A7359] text-[#6A7359] hover:bg-[#6A7359]/10" 
                  data-testid="email-quote-btn"
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Email
                </Button>
              </div>
              <Button 
                variant="outline" 
                className="w-full mb-2 border-[#6A7359] text-[#6A7359] hover:bg-[#6A7359]/10" 
                onClick={onDownloadPDF} 
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button 
                className="w-full bg-[#6D7175] hover:bg-[#5A5E61] text-white" 
                onClick={onProceed} 
                data-testid="proceed-order-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-[#999]">
              <Calculator className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Add doors to see quote</p>
            </div>
          )}
          
          <p className="text-xs text-[#999] mt-4 text-center">Any adjustments can be made when we send you an Order Review to confirm the details of your order.</p>
        </CardContent>
      </Card>
    </div>
  );
}
